const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return 'Just now';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return 'Just now';
  if (diffMs < MINUTE_MS) return 'Just now';
  if (diffMs < HOUR_MS) {
    const mins = Math.floor(diffMs / MINUTE_MS);
    return mins === 1 ? '1 min ago' : `${mins} min ago`;
  }
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (diffMs < 2 * DAY_MS) return 'Yesterday';
  if (diffMs < 7 * DAY_MS) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatMessageTime(dateString?: string): string {
  if (!dateString) return 'Just now';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < MINUTE_MS) return 'Just now';
  if (diffMs < HOUR_MS) {
    const mins = Math.floor(diffMs / MINUTE_MS);
    return mins === 1 ? '1 min ago' : `${mins} min ago`;
  }
  if (diffMs < DAY_MS) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (diffMs < 2 * DAY_MS) {
    return `Yesterday ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Format for chat sticky header: "Today", "Yesterday", "Mon, 4 Feb", or "4 Feb 2025" */
export function formatChatHeaderDate(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (dateOnly.getTime() === today.getTime()) {
    return 'Today';
  }
  if (dateOnly.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatDate(dateString?: string): {
  date: string;
  fullDate: string;
} {
  if (!dateString) {
    return { date: '', fullDate: '' };
  }
  const date = new Date(dateString);
  const datePart = date.toISOString().split('T')[0];
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };
  const formatted = date.toLocaleString('en-US', options).replace(',', '');
  return { date: datePart, fullDate: formatted };
}
export function getRangeFromPeriod(period: string): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];
  let startDate = endDate;

  switch (period) {
    case 'today':
      startDate = endDate;
      break;
    case '7days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      startDate = d.toISOString().split('T')[0];
      break;
    }
    case '30days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      startDate = d.toISOString().split('T')[0];
      break;
    }
    case '1year': {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      startDate = d.toISOString().split('T')[0];
      break;
    }
    default:
      startDate = endDate;
  }

  return { startDate, endDate };
}

/** Start of range for client-side list filtering (sales, billing, etc.). */
export function getFilterDateFromPeriod(period: string): Date {
  const now = new Date();
  const filterDate = new Date();

  switch (period) {
    case 'today':
      filterDate.setHours(0, 0, 0, 0);
      break;
    case '7days':
      filterDate.setDate(now.getDate() - 7);
      break;
    case '30days':
      filterDate.setDate(now.getDate() - 30);
      break;
    case '1year':
      filterDate.setFullYear(now.getFullYear() - 1);
      break;
    default:
      filterDate.setHours(0, 0, 0, 0);
  }

  return filterDate;
}
